import { Coins, Infinity as InfinityIcon, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { ProductKey } from '../../lib/app-registry';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';

interface AiCreditsPanelProps {
  productKey: ProductKey;
  isAdmin: boolean;
}

/**
 * The only place token-to-credit conversion happens -- `workspace_ai_credits` and every backend
 * figure stay in raw tokens (see ai-credit-system's design.md: precise metering, no per-call
 * rounding loss). Credits are a display-only abstraction so a user never has to reason in
 * millions of tokens; 1 credit = 1,000 tokens, matching the pack pricing's own unit.
 */
const TOKENS_PER_CREDIT = 1000;

/** Floored, never rounded up -- a displayed balance must never overstate what's left. */
function formatCredits(tokens: number): string {
  return new Intl.NumberFormat().format(Math.floor(tokens / TOKENS_PER_CREDIT));
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * A workspace's hosted-key AI token balance plus the three purchasable top-up packs — see the
 * `ai-credit-purchasing` capability. Rendered per-product, same convention as
 * `SubscriptionBillingPanel`: each portal's `BillingSettingsSection` passes its own `productKey`.
 */
export function AiCreditsPanel({ productKey, isAdmin }: AiCreditsPanelProps) {
  const { data: balance, isLoading: isBalanceLoading } = trpc.aiCredits.getBalance.useQuery({
    productKey,
  });
  const { data: packs } = trpc.aiCredits.listPacks.useQuery();

  const createCheckout = trpc.aiCredits.createCheckout.useMutation({
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start checkout.');
    },
  });

  if (!isAdmin) return null;

  const totalBalance = (balance?.allowanceBalance ?? 0) + (balance?.purchasedBalance ?? 0);

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden mt-6">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-primary" />
          <CardTitle>AI Credits</CardTitle>
        </div>
        <CardDescription>
          Hosted-key AI usage for this workspace draws from a daily credit allowance, then a
          purchased balance. Connect your own API key in AI Provider settings to use AI beyond this
          balance.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {isBalanceLoading ? (
          <div className="h-16 bg-muted/30 rounded-lg animate-pulse" />
        ) : balance?.uncapped ? (
          <div className="flex items-center gap-2 p-4 rounded-xl border border-border/40 bg-background/50">
            <InfinityIcon className="h-5 w-5 text-primary" />
            <span className="font-medium">Uncapped on this plan — no balance is tracked.</span>
          </div>
        ) : (
          <div className="p-4 rounded-xl border border-border/40 bg-background/50 space-y-1">
            <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <span>Remaining Balance</span>
              <Sparkles className="h-4 w-4 text-amber-500" />
            </div>
            <div className="text-2xl font-extrabold tracking-tight">
              {formatCredits(totalBalance)} <span className="text-sm font-normal">credits</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatCredits(balance?.allowanceBalance ?? 0)} from today's allowance +{' '}
              {formatCredits(balance?.purchasedBalance ?? 0)} purchased
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          {(packs ?? []).map((pack) => (
            <div
              key={pack.code}
              className="flex flex-col gap-3 p-4 rounded-xl border border-border/40 bg-background/50"
            >
              <div>
                <div className="font-semibold">{pack.label}</div>
                <Badge variant="outline" className="mt-1">
                  {formatCredits(pack.tokens)} credits
                </Badge>
              </div>
              <div className="text-xl font-bold">
                {formatMoney(pack.amountCents, pack.currency)}
              </div>
              <Button
                variant="outline"
                disabled={createCheckout.isPending || !pack.configured}
                onClick={() => createCheckout.mutate({ productKey, packCode: pack.code })}
              >
                {createCheckout.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : pack.configured ? (
                  'Buy credits'
                ) : (
                  'Not available yet'
                )}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
